/**
 * app_config key specs — the single source of truth for known config keys.
 *
 * SHARED between admin client and server so validation and UI render off the
 * same map. Keys not in this map are "unknown" and accept any string (the
 * admin renders a free-text editor and the server skips type validation) —
 * this lets us add a key from the DB without touching code, at the cost of
 * no per-type UX until it's declared here.
 *
 * All stored values are strings in the DB (app_config.config_value TEXT).
 * The `type` determines both the client editor and how the server parses
 * the string for range checks. Boolean keys store the literal 'true' /
 * 'false'; percent stores a decimal string like '0.9' (not '90').
 */
export const CONFIG_KEY_SPECS = {
    clip_watched_coverage_ratio: {
        type: 'percent',
        label: 'אחוז הכיסוי לסימון קליפ כנצפה',
        description: 'אחוז מסרטון שהמשתמש צריך לצפות בו (בעמדות שונות ולא בסך הכל) כדי שהקליפ יסומן כנצפה.',
        min: 0.50,
        max: 1.00,
        step: 0.05,
    },
    anonymous_daily_view_limit: {
        type: 'integer',
        label: 'מגבלת צפיות יומית לאנונימי',
        description: 'כמות ניסיונות צפייה מותרים ליום למכשיר לא מזוהה (לפי fingerprint).',
        min: 1,
    },
    enforce_purchases: {
        type: 'boolean',
        label: 'אכיפת רכישות',
        description: 'אם דלוק — גישה לשיעורים בתשלום דורשת רכישה בפועל. אם כבוי — כל השיעורים פתוחים (מצב פיתוח).',
    },
};
/**
 * Snap a decimal to the nearest step-multiple. Numeric noise from JS floats
 * (0.1 + 0.2 = 0.30000000000000004) is scaled out by multiplying up to
 * integers first, then dividing back.
 */
export function snapPercent(value, step) {
    const grid = Math.round(1 / step); // e.g. 20 for 0.05 step
    return Math.round(value * grid) / grid;
}
/**
 * Canonical string representation for a percent value in storage: the
 * shortest decimal that round-trips (0.5 → "0.5", 0.55 → "0.55"). Matches
 * the pre-existing seed style ("0.9" not "0.90"), so the migration to
 * typed editors doesn't reformat every row on first save.
 */
export function formatPercentForStorage(value) {
    const snapped = snapPercent(value, 0.05);
    // Cap to 2 decimals since our smallest step is 0.05.
    return String(Math.round(snapped * 100) / 100);
}
/**
 * Validate a stored string value against its spec. Unknown keys pass
 * unconditionally (free-text). Returns null when valid.
 *
 * Used by BOTH the admin client (pre-save UX) and the server (final guard).
 * Keep it pure, no side effects.
 */
export function validateConfigValue(key, value) {
    const spec = CONFIG_KEY_SPECS[key];
    if (!spec)
        return null;
    if (spec.type === 'percent') {
        const n = Number(value);
        if (!Number.isFinite(n)) {
            return { code: 'config_invalid_percent', message: `${key}: ערך אחוז חייב להיות מספר עשרוני (למשל 0.9)` };
        }
        if (n < spec.min || n > spec.max) {
            return {
                code: 'config_out_of_range',
                message: `${key}: מחוץ לטווח (${percent(spec.min)}%–${percent(spec.max)}%)`,
            };
        }
        // Snap check — the value on the wire must land on a step boundary.
        // Small epsilon tolerance for float noise.
        const snapped = snapPercent(n, spec.step);
        if (Math.abs(snapped - n) > 1e-9) {
            return {
                code: 'config_bad_step',
                message: `${key}: הערך חייב להיות בקפיצות של ${percent(spec.step)}%`,
            };
        }
        return null;
    }
    if (spec.type === 'integer') {
        if (!/^-?\d+$/.test(value.trim())) {
            return { code: 'config_not_integer', message: `${key}: ערך חייב להיות מספר שלם` };
        }
        const n = Number(value);
        if (spec.min !== undefined && n < spec.min) {
            return { code: 'config_out_of_range', message: `${key}: מינימום ${spec.min}` };
        }
        if (spec.max !== undefined && n > spec.max) {
            return { code: 'config_out_of_range', message: `${key}: מקסימום ${spec.max}` };
        }
        return null;
    }
    if (spec.type === 'boolean') {
        if (value !== 'true' && value !== 'false') {
            return { code: 'config_not_boolean', message: `${key}: ערך חייב להיות true או false` };
        }
        return null;
    }
    return null;
}
function percent(v) {
    return Math.round(v * 100);
}
